import { useCallback, useEffect, useState } from "react";

/**
 * Real offline-readiness check + active warm-up.
 *
 * The build emits /offline-manifest.json listing every file the app needs to
 * run with no network. We verify each one against Cache Storage, and can force
 * a download of anything missing (iOS in particular will evict caches or stop
 * an install part-way, leaving a half-cached app).
 */

export type OfflineReadyStatus =
  | "checking"
  | "ready"
  | "partial"
  | "not-ready"
  | "unsupported";

export interface OfflineReadyResult {
  status: OfflineReadyStatus;
  cachedAssets: number;
  requiredAssets: number;
  totalCacheEntries: number;
  shellCached: boolean;
  swActive: boolean;
  checkedAt: number | null;
  checking: boolean;
  /** Warm-up in progress. */
  downloading: boolean;
  /** 0-100 progress of the warm-up. */
  progress: number;
  recheck: () => void;
  /** Fetch every missing asset so the worker stores it, then re-check. */
  downloadAll: () => Promise<void>;
}

/** Files listed by the build manifest, falling back to what this page loaded. */
async function collectRequiredAssets(): Promise<string[]> {
  const origin = window.location.origin;
  const urls = new Set<string>();

  try {
    const res = await fetch("/offline-manifest.json", { cache: "no-store" });
    if (res.ok) {
      const data = (await res.json()) as { files?: string[] };
      for (const f of data.files ?? []) urls.add(new URL(f, origin).href);
    }
  } catch {
    /* offline or manifest not deployed yet — fall back below */
  }

  try {
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    for (const entry of entries) {
      if (!entry.name.startsWith(origin)) continue;
      if (!/\.(js|css|woff2?|ttf)(\?|$)/i.test(entry.name)) continue;
      urls.add(entry.name.split("?")[0]);
    }
  } catch {
    /* performance API unavailable */
  }

  document.querySelectorAll<HTMLScriptElement>("script[src]").forEach((el) => {
    if (el.src.startsWith(origin)) urls.add(el.src.split("?")[0]);
  });
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
    .forEach((el) => {
      if (el.href.startsWith(origin)) urls.add(el.href.split("?")[0]);
    });

  return [...urls];
}

async function matchAny(url: string): Promise<boolean> {
  return Boolean(await caches.match(url, { ignoreSearch: true }));
}

export function useOfflineReady(): OfflineReadyResult {
  const [state, setState] = useState<
    Omit<OfflineReadyResult, "recheck" | "downloadAll">
  >({
    status: "checking",
    cachedAssets: 0,
    requiredAssets: 0,
    totalCacheEntries: 0,
    shellCached: false,
    swActive: false,
    checkedAt: null,
    checking: true,
    downloading: false,
    progress: 0,
  });

  const run = useCallback(async () => {
    setState((s) => ({ ...s, checking: true }));

    if (!("caches" in window) || !("serviceWorker" in navigator)) {
      setState((s) => ({
        ...s,
        status: "unsupported",
        cachedAssets: 0,
        requiredAssets: 0,
        totalCacheEntries: 0,
        shellCached: false,
        swActive: false,
        checkedAt: Date.now(),
        checking: false,
      }));
      return;
    }

    let swActive = false;
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      swActive = regs.some((r) => r.active || r.waiting || r.installing);
    } catch {
      swActive = false;
    }

    let totalCacheEntries = 0;
    try {
      const names = await caches.keys();
      for (const name of names) {
        const cache = await caches.open(name);
        totalCacheEntries += (await cache.keys()).length;
      }
    } catch {
      /* ignore */
    }

    const origin = window.location.origin;
    const shellCandidates = [`${origin}/index.html`, `${origin}/`, "/index.html", "/"];
    let shellCached = false;
    for (const candidate of shellCandidates) {
      try {
        if (await matchAny(candidate)) {
          shellCached = true;
          break;
        }
      } catch {
        /* ignore */
      }
    }

    const required = await collectRequiredAssets();
    let cachedAssets = 0;
    for (const url of required) {
      try {
        if (await matchAny(url)) cachedAssets += 1;
      } catch {
        /* ignore */
      }
    }

    const ratio = required.length ? cachedAssets / required.length : 0;
    let status: OfflineReadyStatus;
    if (!swActive && totalCacheEntries === 0) {
      status = "not-ready";
    } else if (shellCached && required.length > 0 && ratio >= 0.95) {
      status = "ready";
    } else if (shellCached || cachedAssets > 0) {
      status = "partial";
    } else {
      status = "not-ready";
    }

    setState((s) => ({
      ...s,
      status,
      cachedAssets,
      requiredAssets: required.length,
      totalCacheEntries,
      shellCached,
      swActive,
      checkedAt: Date.now(),
      checking: false,
    }));
  }, []);

  const downloadAll = useCallback(async () => {
    if (!("caches" in window)) return;
    setState((s) => ({ ...s, downloading: true, progress: 0 }));
    try {
      const required = await collectRequiredAssets();
      const missing: string[] = [];
      for (const url of required) {
        if (!(await matchAny(url))) missing.push(url);
      }
      // Always re-pull the shell so a cold launch works.
      missing.push(`${window.location.origin}/index.html`);

      let done = 0;
      for (const url of missing) {
        try {
          // Goes through the service worker, which stores it in app-assets.
          await fetch(url, { cache: "reload" });
        } catch {
          /* keep going — one failure shouldn't abort the warm-up */
        }
        done += 1;
        setState((s) => ({
          ...s,
          progress: Math.round((done / missing.length) * 100),
        }));
      }
    } finally {
      setState((s) => ({ ...s, downloading: false, progress: 100 }));
      await run();
    }
  }, [run]);

  useEffect(() => {
    const t = window.setTimeout(() => void run(), 600);
    return () => window.clearTimeout(t);
  }, [run]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onChange = () => void run();
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
    return () =>
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
  }, [run]);

  return { ...state, recheck: () => void run(), downloadAll };
}
